import React, { useState, useEffect } from 'react';
import Table from '../../components/Table';
import PageScroller from '../../components/PageScroller';
import { SaveCancel } from '../../components/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { securePost } from '../../config/api';
import { formatDateTime, formatAmount } from '../../utils/format';

const AttachPopup = ({ open, onClose, payment, onAttachSuccess, clientId }) => {
  const { t } = useTranslation();
  const [BLs, setBLs] = useState([]);
  const [blInputs, setBlInputs] = useState([]);
  const [selectedBLs, setSelectedBLs] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [activeTab2, setActiveTab2] = useState('recent');
  const [blSummary, setBlSummary] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saving, setSaving] = useState(false);

  const toAttach = (payment?.amount || 0) - (payment?.totalAttachedAmount || 0);

  const getRowBounds = (row) => {
    const totalAttached = Number(row.totalAttachedAmount) || 0;
    const priceTTC = Number(row.priceTTC) || 0;
    const baseRemaining = priceTTC - totalAttached;
    const isCredit = baseRemaining < 0;
    const min = isCredit ? baseRemaining : 0;
    const max = isCredit ? 0 : baseRemaining;
    return { isCredit, min, max };
  };

  const getDisplayForRow = (rowId, row) => {
    const input = blInputs.find(i => i.id === rowId);
    const { isCredit } = getRowBounds(row);
    return input?.toSettleDisplay ??
      (input?.toSettle != null ? String(input.toSettle) : (isCredit ? '-0' : ''));
  };

  const getLiveToSettle = (row) => {
    const { min, max, isCredit } = getRowBounds(row);
    const display = getDisplayForRow(row.id, row);

    if (display === '' || display === '-') return 0;

    const parsed = Number(display.replace(',', '.'));
    if (isNaN(parsed)) return 0;

    return Math.min(Math.max(parsed, min), max);
  };

  const finalizeToSettle = (row, rawString) => {
    const { min, max, isCredit } = getRowBounds(row);

    let v = (rawString || '').replace(',', '.');
    if (v === '' || v === '-') v = isCredit ? '-0' : '0';

    let n = Number(v);
    if (isNaN(n)) n = isCredit ? -0 : 0;

    n = Math.min(Math.max(n, min), max);

    setBlInputs(prev => {
      const currentTotalOthers = prev.reduce((sum, it) =>
        it.id === row.id ? sum : sum + (Number(it.toSettle) || 0), 0
      );
      const maxForThis = Math.max(toAttach - currentTotalOthers, 0);
      const clampedByBudget = Math.min(n, maxForThis);

      return prev.map(x =>
        x.id === row.id
          ? { ...x, toSettle: clampedByBudget, toSettleDisplay: String(clampedByBudget) }
          : x
      );
    });
  };

  const resetRowInput = (row) => {
    const { isCredit } = getRowBounds(row);
    setBlInputs(prev =>
      prev.map(x =>
        x.id === row.id
          ? { ...x, toSettle: 0, toSettleDisplay: isCredit ? '-0' : '0' }
          : x
      )
    );
  };

  const handleToSettleChange = (id, patch) => {
    setBlInputs(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
  };

  const handleCheckboxChange = (row) => {
    setSelectedBLs((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (exists) {
        resetRowInput(row);
        return prev.filter((item) => item.id !== row.id);
      } else {
        resetRowInput(row);
        return [...prev, row];
      }
    });
  };

  const columns = [
    {
      label: '',
      key: 'checkbox',
      render: (_, row) => (
        <input
          type="checkbox"
          checked={selectedBLs.some((item) => item.id === row.id)}
          onChange={() => handleCheckboxChange(row)}
        />
      ),
    },
    { label: t(`columns.deliveryNoteNumber`), key: 'deliveryNoteNumber' },
    {
      label: t(`columns.createdAt`),
      key: 'createdAt',
      render: (date) => formatDateTime(date),
    },
    {
      label: 'Initial',
      key: 'priceTTC',
      render: (_, row) => formatAmount(Number(row.priceTTC) || 0),
    },
    {
      label: t(`columns.settled`),
      key: 'settled',
      render: (_, row) => {
        const totalAttached = Number(row.totalAttachedAmount) || 0;
        const live = getLiveToSettle(row);
        return formatAmount(totalAttached + live);
      },
    },
    {
      label: t(`columns.notSettled`),
      key: 'nonSettled',
      render: (_, row) => {
        const totalAttached = Number(row.totalAttachedAmount) || 0;
        const priceTTC = Number(row.priceTTC) || 0;
        const live = getLiveToSettle(row);
        return formatAmount(priceTTC - (totalAttached + live));
      },
    },
    {
      label: t(`columns.toSettle`),
      key: 'toSettle',
      render: (_, row) => {
        const { isCredit, min, max } = getRowBounds(row);
        const display = getDisplayForRow(row.id, row);
        const allow = /^-?\d*(?:[.,]\d*)?$/;

        return (
          <input
            type="text"
            inputMode="decimal"
            pattern="-?[0-9]*[.,]?[0-9]*"
            className="border rounded px-2 py-1 w-full text-sm"
            value={display}
            onChange={(e) => {
              const allow = /^-?\d*(?:[.,]\d*)?$/;
              const { isCredit, min, max } = getRowBounds(row);

              let v = e.target.value;

              if (v === '' || v === '-') {
                handleToSettleChange(row.id, { toSettleDisplay: v });
                return;
              }

              if (!allow.test(v)) return;
              const normalized = v.replace(',', '.');

              const withSign = isCredit
                ? (normalized.startsWith('-') ? normalized : '-' + normalized.replace(/^-/, ''))
                : normalized.replace(/^-/, '');

              const n = Number(withSign);
              if (!isNaN(n)) {
                const clamped = Math.min(Math.max(n, min), max);

                if (clamped !== n) {
                  handleToSettleChange(row.id, { toSettleDisplay: String(clamped) });
                  return;
                }

                handleToSettleChange(row.id, { toSettleDisplay: withSign });
                return;
              }

              handleToSettleChange(row.id, { toSettleDisplay: withSign });
            }}

            onBlur={(e) => finalizeToSettle(row, e.target.value)}
            disabled={!selectedBLs.some((item) => item.id === row.id)}
            onFocus={(e) => {
              if (e.target.value === '-0' || e.target.value === '0') {
                e.target.select();
              }
            }}
          />
        );
      },
    },
  ];

  const fetchData = async (page = 0, size = pageSize) => {
    const showSnackbar = () => {};
    const payload = { clientId, newestFirst: activeTab2 === 'recent' };
    try {
      const [pagedRes, sumRes] = await Promise.all([
        securePost(`deliveryNotes/attacheableCartsPaged`, payload, showSnackbar, {
          params: { page, size },
        }),
        securePost(`/deliveryNotes/attacheableCartsSum`, payload, showSnackbar),
      ]);

      const bls = pagedRes?.data?.content || [];
      setBLs(bls);
      setTotalPages(pagedRes?.data?.totalPages || 0);
      setCurrentPage(pagedRes?.data?.number || 0);

      const initInputs = bls.map((bl) => {
        const totalAttached = Number(bl.totalAttachedAmount) || 0;
        const priceTTC = Number(bl.priceTTC) || 0;
        const baseRemaining = priceTTC - totalAttached;
        const isCredit = baseRemaining < 0;
        return {
          id: bl.id,
          toSettle: 0,
          toSettleDisplay: isCredit ? '-0' : '0',
        };
      });
      setBlInputs(initInputs);

      setBlSummary(sumRes?.data?.totalAmount || 0);
      setSelectedBLs([]);
    } catch (error) {
      console.error('Failed to fetch BLs or summary:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData(currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab2, currentPage, pageSize, clientId]);

  const handleCancel = () => {
    setSelectedBLs([]);
    setBlInputs([]);
    onClose();
  };

  const handleSubmit = async () => {
    const cartPayments = selectedBLs.map((bl) => {
      const attached = getLiveToSettle(bl);
      return {
        amount: payment.amount,
        attachedAmount: attached,
        deliveryNoteCreatedAt: bl.createdAt,
        deliveryNoteId: bl.id,
        deliveryNoteNumber: bl.deliveryNoteNumber,
        forced: false,
        paymentId: payment.id,
        reference: payment.reference,
        type: payment.type,
      };
    });

    const payload = {
      cartPayments,
      note: newComment,
      id: payment.id,
    };

    setSaving(true);
    try {
      const showSnackbar = () => {};
      await securePost(`payments/attachCarts`, payload, showSnackbar);
      onAttachSuccess?.();
      handleCancel();
    } catch (error) {
      console.error('Failed to attach BLs:', error);
      setSnackbar({
        open: true,
        message: t(`errors.attachFailed`),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
      <div className="bg-white rounded-xl p-6 w-11/12 max-w-6xl max-h-[70vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1">
            <h3 className="text-[22px] font-semibold text-[#ACAFC6]">
              {t(`fields_types.attach`)}
            </h3>
            <h3 className="text-[22px] text-[#6B78F8] font-semibold">
              ({selectedBLs.length})
            </h3>
          </div>

          <div className="flex items-center gap-4">
            <h3 className="text-[#043F88] text-[14px] font-semibold">
              {t(`filters.products.sortBy`)}:
            </h3>
            <div className="flex justify-end bg-gray-200 rounded-[12px] p-1 w-fit">
              <button
                className={`px-6 py-2 text-[14px] font-bold rounded-[12px] ${activeTab2 === 'recent'
                    ? 'bg-white text-[#6B78F8] shadow-md'
                    : 'bg-transparent text-[#ACAFC6]'
                  }`}
                onClick={() => setActiveTab2('recent')}
              >
                {t(`options.newest`)}
              </button>
              <button
                className={`px-6 py-2 text-[14px] font-bold rounded-[12px] ${activeTab2 === 'old'
                    ? 'bg-[#043F88] text-white shadow-md'
                    : 'bg-transparent text-[#ACAFC6]'
                  }`}
                onClick={() => setActiveTab2('old')}
              >
                {t(`options.oldest`)}
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Table */}
        <div className="overflow-y-auto flex-1">
          <Table columns={columns} data={BLs} />
          <PageScroller
            pageNumber={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex text-left">
            <p className="text-[#043F88] text-[16px] font-bold pl-2">
              {formatAmount(blSummary)}
            </p>
          </div>
          <div className="space-x-4">
            <SaveCancel onCancelClick={handleCancel} onSaveClick={handleSubmit} saving={saving} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachPopup;
